import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, MapPin, ChevronDown, Plus, Trash2 } from 'lucide-react';
import { searchAirports, airportByCode } from '../data/airports';
import { addCustomAirport, removeCustomAirport, onCustomAirportsChange } from '../data/customAirports';

/**
 * Searchable airport picker — type a code, a city or a country.
 *
 * Not a native <select>: 182 options in one is unusable, and people know their
 * airport by city far more often than by IATA code.
 *
 * THE LIST IS RENDERED IN A PORTAL. Absolutely positioning it inside the
 * component put it inside the dialog's scroll container, which clipped it — the
 * options below the fold were simply cut off. A portal escapes every clipping
 * ancestor, at the cost of having to position it manually against the input and
 * follow it on scroll and resize.
 *
 * The catalogue is curated rather than exhaustive, so a missing airport must be
 * an inconvenience and not a wall. Type something it does not know and it offers
 * to ADD it — code, city and country — after which it behaves exactly like a
 * built-in entry: searchable, labelled, and rendered by name everywhere rather
 * than as a bare code. Saved entries can be removed again from the same list.
 *
 * The server validates the code's format only, so nothing downstream depends on
 * an airport being one we happen to know about.
 */
const miniInput = {
  width: '100%', background: '#12122a', border: '1px solid #2a2a4a', borderRadius: 7,
  color: '#ccc', padding: '7px 9px', fontSize: 12, fontFamily: 'inherit',
};
const miniPrimary = {
  flex: 1, background: '#f97316', color: '#1a1204', border: 'none', borderRadius: 7,
  padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
};
const miniGhost = {
  background: 'transparent', color: '#888', border: '1px solid #333', borderRadius: 7,
  padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};

export default function AirportSelect({ value, onChange, placeholder = 'City or code', autoFocus }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [rect, setRect] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();

  // Bumped whenever the custom list changes, so an open picker re-runs its
  // search instead of showing a stale catalogue.
  const [customVersion, setCustomVersion] = useState(0);
  useEffect(() => onCustomAirportsChange(() => setCustomVersion((v) => v + 1)), []);

  const [adding, setAdding] = useState(null);   // { code, city, country } | null
  const [addErr, setAddErr] = useState(null);

  const selected = airportByCode(value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const results = useMemo(() => searchAirports(query), [query, customVersion]);

  const raw = query.trim().toUpperCase();
  const canAdd = /^[A-Z]{3}$/.test(raw) && !results.some((a) => a.code === raw);
  const options = results;

  const measure = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Widen past the input when it is narrow — the field sits in a two-column
    // grid, and a city plus a country does not fit in half a dialog.
    const width = Math.max(r.width, 260);
    // Flip above the input when there is not enough room below it.
    const below = window.innerHeight - r.bottom;
    const flip = below < 240 && r.top > below;
    setRect({ left: Math.min(r.left, window.innerWidth - width - 8), top: r.bottom + 5, bottom: window.innerHeight - r.top + 5, width, flip });
  }, []);

  useLayoutEffect(() => { if (open) measure(); }, [open, measure, options.length]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => measure();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, measure]);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => { setCursor(0); }, [query]);

  useEffect(() => {
    const el = listRef.current?.children?.[cursor];
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  const choose = (a) => {
    onChange(a.code);
    setQuery('');
    setOpen(false);
  };

  const saveCustom = () => {
    const res = addCustomAirport(adding);
    if (!res.ok) { setAddErr(res.error); return; }
    setAdding(null);
    setAddErr(null);
    choose(res.airport);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { e.preventDefault(); setOpen(true); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (options[cursor]) choose(options[cursor]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
  };

  const display = open ? query : (selected ? `${selected.code} · ${selected.city}` : (value || ''));

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#555', pointerEvents: 'none' }} />
        <input
          ref={inputRef}
          value={display}
          // Opens on click or on typing — not on focus. Tabbing through the
          // dialog should not fling a list open over the fields below.
          onClick={() => { if (!open) { setQuery(''); setOpen(true); } }}
          onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          style={{
            width: '100%', background: '#0f0f23', border: `1px solid ${open ? '#f9731660' : '#1e1e3a'}`,
            borderRadius: 9, color: '#ccc', padding: '9px 28px 9px 28px', fontSize: 13, fontFamily: 'inherit',
          }}
        />
        <ChevronDown size={13} style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', color: '#555', pointerEvents: 'none' }} />
      </div>

      {open && rect && createPortal(
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          style={{
            position: 'fixed', left: rect.left, width: rect.width,
            ...(rect.flip ? { bottom: rect.bottom } : { top: rect.top }),
            zIndex: 10050, maxHeight: 260, overflowY: 'auto',
            background: '#0f0f23', border: '1px solid #2a2a4a', borderRadius: 10,
            boxShadow: '0 14px 34px #00000080', padding: 4,
          }}
        >
          {adding ? (
            <div style={{ padding: 10 }}>
              <div style={{ color: '#ccc', fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                Add {adding.code}
              </div>
              {addErr && <div style={{ color: '#ef4444', fontSize: 11, marginBottom: 7 }}>{addErr}</div>}
              <input
                autoFocus
                value={adding.city}
                onChange={(e) => setAdding((a) => ({ ...a, city: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveCustom(); } if (e.key === 'Escape') { e.preventDefault(); setAdding(null); } }}
                placeholder="City or airport name"
                style={miniInput}
              />
              <input
                value={adding.country}
                onChange={(e) => setAdding((a) => ({ ...a, country: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveCustom(); } if (e.key === 'Escape') { e.preventDefault(); setAdding(null); } }}
                placeholder="Country (optional)"
                style={{ ...miniInput, marginTop: 6 }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
                <button type="button" onClick={saveCustom} style={miniPrimary}>Save airport</button>
                <button type="button" onClick={() => { setAdding(null); setAddErr(null); }} style={miniGhost}>Cancel</button>
              </div>
            </div>
          ) : (
          <>
          {canAdd && (
            <button
              type="button"
              onClick={() => { setAdding({ code: raw, city: '', country: '' }); setAddErr(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, width: '100%',
                background: 'transparent', border: 'none', borderRadius: 7,
                padding: '9px 10px', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                color: '#f97316', fontSize: 12.5, fontWeight: 700,
              }}
            >
              <Plus size={13} /> Add “{raw}” as a new airport
            </button>
          )}

          {options.length === 0 && !canAdd && (
            <div style={{ color: '#666', fontSize: 12, padding: '12px 10px', lineHeight: 1.5 }}>
              No match. Type a three-letter code to add it yourself.
            </div>
          )}
          {options.map((a, i) => (
            <button
              key={a.code + (a.custom ? '-custom' : '')}
              type="button"
              role="option"
              aria-selected={i === cursor}
              onMouseEnter={() => setCursor(i)}
              onClick={() => choose(a)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                background: i === cursor ? '#f9731615' : 'transparent',
                border: 'none', borderRadius: 7, padding: '7px 10px', cursor: 'pointer',
                textAlign: 'left', fontFamily: 'inherit',
              }}
            >
              <span style={{
                color: '#f97316', fontSize: 11, fontWeight: 800, fontFamily: 'monospace',
                width: 32, flexShrink: 0,
              }}>{a.code}</span>
              {/* City above country rather than beside it. Side by side, a long
                  country name is what survives and the city collapses to nothing,
                  which is exactly backwards — the city is what people search for. */}
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <span style={{ color: '#ccc', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.city}
                </span>
                <span style={{ color: '#555', fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <MapPin size={9} style={{ flexShrink: 0 }} /> {a.country}
                </span>
              </span>
              {a.custom && (
                <span
                  role="button"
                  tabIndex={-1}
                  title="Remove this airport"
                  onClick={(e) => { e.stopPropagation(); removeCustomAirport(a.code); }}
                  style={{ color: '#444', flexShrink: 0, padding: 3, display: 'inline-flex' }}
                >
                  <Trash2 size={11} />
                </span>
              )}
            </button>
          ))}
          </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
